import React, { useState, useRef, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaFile,
  FaTrash,
  FaGear,
  FaCloudArrowUp,
  FaUpload,
  FaCheck,
  FaSpinner,
  FaDownload,
} from 'react-icons/fa6';
import { CSVFileEntry } from '../../../../types/common';
import { csvFilesApi } from '../../../../api/services/csvFilesApi';
import { ColorTheme } from './CSVFileHeader';

interface CompactFileCardProps {
  entry: CSVFileEntry;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInputChange: (field: keyof CSVFileEntry, value: string) => void;
  onRemove: () => void;
  onUpload?: () => void;
  onOpenSettings: () => void;
  onSave?: () => void;
  isUploading?: boolean;
  isDirty?: boolean;
  colorTheme?: ColorTheme;
  hasDuplicateBezeichnung?: boolean;
  sessionId?: string;
  indexLabel?: string;
}

// Color theme configuration
const themeClasses = {
  blue: {
    accent: 'bg-primary',
    accentLight: 'bg-accent',
    icon: 'text-primary',
    focusRing: 'focus:ring-ring/30 focus:border-ring',
    dropBorder: 'border-primary',
    dropBg: 'bg-accent',
    dropText: 'text-primary',
    settingsBg: 'hover:bg-accent',
  },
  green: {
    accent: 'bg-emerald-500',
    accentLight: 'bg-emerald-50',
    icon: 'text-emerald-600',
    focusRing: 'focus:ring-emerald-500/30 focus:border-emerald-400',
    dropBorder: 'border-emerald-400',
    dropBg: 'bg-emerald-50',
    dropText: 'text-emerald-600',
    settingsBg: 'hover:bg-emerald-50',
  },
};

/**
 * Compact single-line file card component
 * Shows: File icon + filename | Bezeichnung input | Status | Settings | Delete
 */
export const CompactFileCard: React.FC<CompactFileCardProps> = ({
  entry,
  onFileChange,
  onInputChange,
  onRemove,
  onUpload,
  onOpenSettings,
  onSave,
  isUploading = false,
  isDirty = false,
  colorTheme = 'blue',
  hasDuplicateBezeichnung = false,
  sessionId,
  indexLabel,
}) => {
  const { t } = useTranslation('training');
  const theme = themeClasses[colorTheme];

  // Drag-drop state
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!entry.storagePath || !entry.fileName) return;
    setIsDownloading(true);
    try {
      const fileType = (entry.type === 'output' ? 'output' : 'input') as 'input' | 'output';
      const result = await csvFilesApi.downloadCSVFile(
        entry.storagePath,
        fileType,
        entry.fileName
      );
      if (!result.success) {
        alert(t('csvFileUploader.errors.downloadFailed', { error: result.error }));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Drag event handlers
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      if (!file.name.toLowerCase().endsWith('.csv')) {
        alert(t('csvFileUploader.dragDrop.invalidFileType'));
        return;
      }

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      const syntheticEvent = {
        target: { files: dataTransfer.files, value: file.name },
        currentTarget: { files: dataTransfer.files, value: file.name },
        preventDefault: () => {},
        stopPropagation: () => {},
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      onFileChange(syntheticEvent);
    }
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const hasFile = entry.file || entry.fileName;

  return (
    <div
      className={`
        group relative bg-card rounded-lg border transition-all duration-150 max-w-2xl w-full
        ${isDragging
          ? `border-dashed ${theme.dropBorder} ring-2 ring-offset-1 ${theme.dropBorder}`
          : 'border-border hover:border-border/60 hover:shadow-sm'
        }
      `}
    >
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 rounded-l-lg ${theme.accent}`} />

      <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 pl-3.5">
        {/* File section - either filename or drop zone */}
        {hasFile ? (
          <div className="flex items-center gap-2 min-w-0 w-32 sm:w-40">
            <FaFile className={`${theme.icon} flex-shrink-0 text-sm`} />
            {indexLabel && (
              <span className={`text-xs font-semibold ${theme.icon} flex-shrink-0`}>
                {indexLabel}
              </span>
            )}
            <span className="text-sm font-medium text-foreground truncate">
              {entry.fileName}
            </span>
          </div>
        ) : (
          <div
            className={`
              flex items-center gap-2 min-w-0 w-32 sm:w-40 px-2.5 py-2 cursor-pointer
              border border-dashed rounded-lg transition-all duration-150
              ${isDragging
                ? `${theme.dropBorder} ${theme.dropBg}`
                : 'border-border hover:border-border/60'
              }
            `}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleDropZoneClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={onFileChange}
              className="hidden"
              aria-label={t('csvFileUploader.a11y.selectFile')}
            />
            <FaCloudArrowUp className={`flex-shrink-0 text-sm ${isDragging ? theme.dropText : 'text-muted-foreground'}`} />
            <span className={`text-xs ${isDragging ? theme.dropText : 'text-muted-foreground'} truncate`}>
              {isDragging
                ? t('csvFileUploader.dragDrop.dropHere')
                : t('csvFileUploader.compact.selectFile', 'Select file')
              }
            </span>
          </div>
        )}

        {/* Bezeichnung input */}
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={entry.bezeichnung}
            onChange={(e) => onInputChange('bezeichnung', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isDirty && onSave && !isUploading && entry.storagePath) {
                e.preventDefault();
                onSave();
              }
            }}
            className={`
              w-full bg-card border rounded-lg px-2.5 py-1.5 text-sm transition-all duration-150
              ${hasDuplicateBezeichnung
                ? 'border-red-400 ring-1 ring-red-200 focus:ring-2 focus:ring-red-500/30 focus:border-red-400'
                : `border-input focus:ring-2 ${theme.focusRing}`
              }
              focus:outline-none
            `}
            placeholder={t('csvFileUploader.placeholder.bezeichnung')}
            aria-label={indexLabel
              ? t('csvFileUploader.a11y.designationFor', { row: indexLabel })
              : t('csvFileUploader.a11y.designation')}
          />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Upload button */}
          {entry.file && !entry.storagePath && onUpload && sessionId && (
            <button
              type="button"
              onClick={onUpload}
              disabled={isUploading || hasDuplicateBezeichnung || !entry.bezeichnung.trim()}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 ${
                isUploading || hasDuplicateBezeichnung || !entry.bezeichnung.trim()
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 active:scale-[0.95]'
              }`}
              title={t('csvFileUploader.table.upload')}
            >
              {isUploading ? (
                <FaSpinner className="animate-spin text-[10px]" />
              ) : (
                <FaUpload className="text-[10px]" />
              )}
            </button>
          )}

          {/* Status icon */}
          {entry.storagePath && (
            isDirty ? (
              <span
                className="w-7 h-7 flex items-center justify-center"
                title={t('csvFileUploader.table.unsavedChanges', 'Unsaved changes - open settings to save')}
              >
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              </span>
            ) : (
              <span className="w-7 h-7 flex items-center justify-center text-emerald-500" title={t('csvFileUploader.messages.fileUploaded')}>
                <FaCheck className="text-[10px]" />
              </span>
            )
          )}

          {/* Download button */}
          {entry.storagePath && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 ${
                isDownloading
                  ? 'bg-muted text-muted-foreground cursor-wait'
                  : `text-muted-foreground ${theme.settingsBg} ${theme.icon.replace('text-', 'hover:text-')}`
              }`}
              title={t('csvFileUploader.compact.download', 'Download file')}
            >
              {isDownloading ? (
                <FaSpinner className="animate-spin text-[10px]" />
              ) : (
                <FaDownload className="text-[10px]" />
              )}
            </button>
          )}

          {/* Settings button */}
          {hasFile && (
            <button
              type="button"
              onClick={onOpenSettings}
              className={`w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground ${theme.settingsBg} ${theme.icon.replace('text-', 'hover:text-')} transition-all duration-150`}
              title={t('csvFileUploader.compact.settings', 'Settings')}
            >
              <FaGear className="text-xs" />
            </button>
          )}

          {/* Delete button */}
          {hasFile && (
            <button
              type="button"
              onClick={onRemove}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-all duration-150"
              title={t('csvFileUploader.removeRow')}
            >
              <FaTrash className="text-[10px]" />
            </button>
          )}
        </div>
      </div>

      {/* Duplicate warning */}
      {hasDuplicateBezeichnung && (
        <div className="px-3.5 pb-2">
          <p className="text-red-500 text-[10px]">
            {t('csvFileUploader.validation.duplicateBezeichnung')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CompactFileCard;
